import { useTranslation } from "react-i18next";
import {
	ChartCard,
	type Column,
	ColumnChart,
	Donut,
	HBarList,
	HeatGrid,
	type HeatGridCell,
	Histogram,
	holatVar,
	Legend,
	LineChart,
	Lollipop,
	ScatterPlot,
	type ScatterPoint,
	SegmentBar,
} from "@/shared/charts";
import type { AnalyticsLayoutProps } from "../../../types";
import { hourWindow, rollingAverage, weeklyShare } from "../../../utils/series";
import { KpiRow, sonMatni } from "../../detail/KpiRow";
import { formatReportCell } from "../../ReportTable";

/**
 * Hududdan chiqishlar analitikasi — canvas «2 — Hududdan chiqishlar»
 * (spec 2026-09-17, reja E2).
 *
 * ⚠️⚠️ **`flagged` — `primary` NING QISMI** (backend `zoneExits.ts`:
 * `primary = count(*)`, `flagged = count(*) FILTER (manba = 'none')`).
 * Shuning uchun «ruxsatli» qatlam HAR JOYDA `total − flagged`: ikkala
 * qiymat ustma-ust qo'yilsa diagramma ruxsatsizlarni IKKI MARTA
 * sanab, jimgina oshirib ko'rsatardi.
 */

const TOP_EMPLOYEES = 8;
const TOP_DEPARTMENTS = 8;
/** `flagged / total` shu ulushdan oshsa scatter nuqtasi qizil (canvas qoidasi). */
const FLAGGED_SHARE = 0.3;
const LONG_BUCKETS = new Set(["60-120", "120+"]);
const SUNDAY = 7;

const RANG = {
	ruxsatli: holatVar("kelgan"),
	ruxsatsiz: holatVar("kelmagan"),
	ortacha: holatVar("neytral"),
} as const;

/**
 * Ruxsat manbasi → rang (canvas donut'i). ⚠️ `manual` — `uzrli`
 * (past kontrast): Donut legendasi qiymatni doim yozadi (relief).
 */
const MANBA_RANGI: Record<string, string> = {
	absence: holatVar("kelgan"),
	task: holatVar("kechikkan"),
	manual: holatVar("uzrli"),
	none: holatVar("kelmagan"),
};

function ikkiXona(soat: number): string {
	return String(soat).padStart(2, "0");
}

function foiz(qiymat: number | null): string {
	return qiymat === null ? "—" : `${sonMatni(qiymat)}%`;
}

/**
 * Shkala yuqori chegarasi — 1/2/5 × 10ⁿ ga yumaloqlanadi, toki o'q
 * belgilari (`0`, `max/2`, `max`) butun va o'qiladigan bo'lsin.
 */
function chiroyliMax(qiymat: number): number {
	if (!(qiymat > 0)) {
		return 1;
	}

	const daraja = 10 ** Math.floor(Math.log10(qiymat));
	const qadam = [1, 2, 5, 10].find((k) => k * daraja >= qiymat) ?? 10;
	const natija = qadam * daraja;
	// `max/2` butun bo'lsin (masalan 5 → 6 emas, 10).
	return natija >= 2 && natija % 2 === 0 ? natija : natija * 2;
}

export function ZoneExitsAnalytics({ kind, data }: AnalyticsLayoutProps) {
	const { t } = useTranslation("reports");
	const bosh = t("analytics.generic.empty");
	const ruxsatliNom = t("analytics.zoneExits.permitted");
	const ruxsatsizNom = t("analytics.zoneExits.unpermitted");
	const kunNomi = (kun: number) => t(`analytics.weekdayShort.${kun}`);

	const totalQiymat = (key: string) => data.totals.find((x) => x.key === key)?.value ?? 0;
	const jamiChiqish = totalQiymat("exits");

	// --- Kunlik ustunlar + 7 kunlik o'rtacha -------------------------
	const ustunlar: Column[] = data.daily.map((kun) => {
		const ruxsatli = Math.max(0, kun.primary - kun.flagged);
		return {
			key: kun.date,
			label: kun.date.slice(8, 10),
			topLabel: sonMatni(kun.primary),
			stack: [
				{ value: ruxsatli, color: RANG.ruxsatli, label: ruxsatliNom },
				{ value: kun.flagged, color: RANG.ruxsatsiz, label: ruxsatsizNom },
			],
			tooltip: t("analytics.zoneExits.dayTooltip", {
				date: kun.date,
				permitted: sonMatni(ruxsatli),
				unpermitted: sonMatni(kun.flagged),
			}),
		};
	});
	const ortachaNom = t("analytics.zoneExits.rollingAverage");
	const ortacha = rollingAverage(
		data.daily.map((kun) => kun.primary),
		7
	);

	// --- Asos (breakdown) va haftalik ulush ---------------------------
	const haftalar = weeklyShare(data.daily);

	// --- Issiqlik xaritasi: hafta kuni × soat ------------------------
	const oyna = hourWindow(data.heat.filter((k) => k.count > 0).map((k) => k.hour));
	const soatlar = Array.from({ length: oyna.to - oyna.from + 1 }, (_, i) => oyna.from + i);
	// ⚠️ Yakshanba faqat ma'lumot bo'lsa — bank ishlamaydigan kun qatori shovqin.
	const yakshanbaBor = data.heat.some((k) => k.weekday === SUNDAY && k.count > 0);
	const kunlar = [1, 2, 3, 4, 5, 6, ...(yakshanbaBor ? [SUNDAY] : [])];
	const kataklar: HeatGridCell[] = data.heat.map((k) => ({
		row: String(k.weekday),
		column: String(k.hour),
		value: k.count,
		tooltip: t("analytics.zoneExits.heatTooltip", {
			weekday: kunNomi(k.weekday),
			hour: ikkiXona(k.hour),
			count: sonMatni(k.count),
		}),
	}));
	const issiqMax = Math.max(0, ...data.heat.map((k) => k.count));
	const choqqiKatak = data.heat.find((k) => k.count === issiqMax && issiqMax > 0);

	// --- Davomiylik gistogrammasi ------------------------------------
	const uzunlar = data.histogram.filter((b) => LONG_BUCKETS.has(b.bucket));
	const uzunJami = uzunlar.reduce((a, b) => a + b.count, 0);
	const uzunRuxsatsiz = uzunlar.reduce((a, b) => a + b.flagged, 0);

	// --- Soatlik chiziq (issiqlik xaritasi bilan bir xil oyna) --------
	const soatlik = data.hourly.filter((h) => h.hour >= oyna.from && h.hour <= oyna.to);
	const soatMax = chiroyliMax(Math.max(0, ...soatlik.flatMap((h) => [h.count, h.previous])));
	const soatChoqqi = soatlik.reduce<(typeof soatlik)[number] | null>(
		(eng, h) => (h.count > 0 && (eng === null || h.count > eng.count) ? h : eng),
		null
	);

	// --- Scatter: xodimlar ------------------------------------------
	const nuqtalar: ScatterPoint[] = data.topEmployees
		.filter((x): x is typeof x & { avg: number } => x.avg !== null)
		.map((x) => {
			const xavfli = x.total > 0 && x.flagged / x.total >= FLAGGED_SHARE;
			return {
				key: x.employeeId,
				x: x.total,
				y: x.avg,
				color: xavfli ? RANG.ruxsatsiz : RANG.ruxsatli,
				tooltip: t("analytics.zoneExits.scatterTooltip", {
					name: x.employeeName,
					total: sonMatni(x.total),
					avg: sonMatni(x.avg),
					flagged: sonMatni(x.flagged),
				}),
			};
		});
	// Bitta e'tiborli nuqta nomlanadi — eng ko'p chiqqan xavfli xodim.
	const nomlanadigan = nuqtalar
		.filter((n) => n.color === RANG.ruxsatsiz)
		.reduce<ScatterPoint | null>((eng, n) => (eng === null || n.x > eng.x ? n : eng), null);
	const xMax = chiroyliMax(Math.max(0, ...nuqtalar.map((n) => n.x)));
	const yMax = chiroyliMax(Math.max(0, ...nuqtalar.map((n) => n.y)));
	const nomlanganNuqtalar = nuqtalar.map((n) =>
		n === nomlanadigan
			? {
					...n,
					label: data.topEmployees.find((x) => x.employeeId === n.key)?.employeeName,
				}
			: n
	);

	return (
		<div data-testid="zone-exits-analytics" className="flex flex-col gap-3.5">
			<KpiRow
				kind={kind}
				totals={data.totals}
				spark={data.spark}
				format={{ outsideMin: (v) => formatReportCell(v, "duration") }}
				danger={["unpermitted"]}
			/>

			<div className="grid gap-3.5 lg:grid-cols-[1.6fr_1fr]">
				<div data-widget="daily">
					<ChartCard
						title={t("analytics.zoneExits.dailyTitle")}
						subtitle={t("analytics.zoneExits.dailySubtitle")}
						extra={
							<Legend
								items={[
									{ label: ruxsatliNom, color: RANG.ruxsatli },
									{ label: ruxsatsizNom, color: RANG.ruxsatsiz },
									{ label: ortachaNom, color: RANG.ortacha, shape: "line" },
								]}
							/>
						}
					>
						<ColumnChart
							columns={ustunlar}
							height={200}
							average={{ values: ortacha, label: ortachaNom }}
							// ⚠️ O'rtacha legendasi sarlavhada (`extra`) — ikkinchi nusxa kerak emas.
							showLegend={false}
							empty={bosh}
						/>
					</ChartCard>
				</div>

				<div data-widget="breakdown">
					<ChartCard title={t("analytics.zoneExits.breakdownTitle")}>
						<Donut
							segments={data.breakdown.map((b) => ({
								key: b.key,
								value: b.count,
								color: MANBA_RANGI[b.key] ?? holatVar("neytral"),
								label: t(`analytics.zoneExits.breakdownLabels.${b.key}`, {
									defaultValue: t(`analytics.breakdown.${b.key}`, { defaultValue: b.key }),
								}),
							}))}
							centerValue={sonMatni(jamiChiqish)}
							centerLabel={t("analytics.zoneExits.breakdownCenter")}
							formatValue={sonMatni}
							empty={bosh}
						/>

						<div
							className="mt-4 mb-2 text-xs"
							style={{ color: "var(--chart-axis)" }}
							data-chart-subtitle
						>
							{t("analytics.zoneExits.weeklyTitle")}
						</div>
						<HBarList
							empty={bosh}
							max={100}
							rows={haftalar.map((hafta, i) => ({
								key: hafta.weekStart,
								label: (
									<span
										title={t("analytics.zoneExits.weekTooltip", {
											from: hafta.weekStart,
											flagged: sonMatni(hafta.flagged),
											total: sonMatni(hafta.primary),
										})}
									>
										{t("analytics.zoneExits.weekLabel", { n: i + 1 })}
									</span>
								),
								value: hafta.share ?? 0,
								valueText: foiz(hafta.share),
								color: RANG.ruxsatsiz,
							}))}
						/>
					</ChartCard>
				</div>
			</div>

			<div className="grid gap-3.5 lg:grid-cols-[1.4fr_1fr]">
				<div data-widget="heat">
					<ChartCard
						title={t("analytics.zoneExits.heatTitle")}
						subtitle={t("analytics.zoneExits.heatSubtitle")}
					>
						{data.heat.length === 0 ? (
							<div className="py-4 text-center text-xs" style={{ color: "var(--chart-axis)" }}>
								{bosh}
							</div>
						) : (
							<>
								<HeatGrid
									rows={kunlar.map((kun) => ({ key: String(kun), label: kunNomi(kun) }))}
									columns={soatlar.map((soat) => ({ key: String(soat), label: ikkiXona(soat) }))}
									cells={kataklar}
									showValues
									legendMinLabel="0"
									legendMaxLabel={sonMatni(issiqMax)}
									empty={bosh}
								/>
								{choqqiKatak ? (
									<div className="mt-2 text-xs" style={{ color: "var(--chart-axis)" }}>
										{t("analytics.zoneExits.heatPeak", {
											weekday: kunNomi(choqqiKatak.weekday),
											hour: ikkiXona(choqqiKatak.hour),
										})}
									</div>
								) : null}
							</>
						)}
					</ChartCard>
				</div>

				<div data-widget="duration">
					<ChartCard
						title={t("analytics.zoneExits.durationTitle")}
						subtitle={t("analytics.zoneExits.durationSubtitle")}
					>
						<Histogram
							height={150}
							empty={bosh}
							bins={
								data.histogram.some((b) => b.count > 0)
									? data.histogram.map((b) => ({
											label: b.bucket,
											count: b.count,
											highlight: LONG_BUCKETS.has(b.bucket),
										}))
									: []
							}
						/>
						{uzunJami > 0 ? (
							<div className="mt-2 text-xs" style={{ color: "var(--chart-axis)" }}>
								{t("analytics.zoneExits.durationLong", {
									total: sonMatni(uzunJami),
									flagged: sonMatni(uzunRuxsatsiz),
								})}
							</div>
						) : null}
					</ChartCard>
				</div>
			</div>

			<div className="grid gap-3.5 lg:grid-cols-3">
				<div data-widget="hourly">
					<ChartCard
						title={t("analytics.zoneExits.hourlyTitle")}
						subtitle={t("analytics.zoneExits.hourlySubtitle")}
					>
						<LineChart
							xLabels={soatlik.map((h) => ikkiXona(h.hour))}
							series={[
								{
									key: "current",
									label: t("analytics.zoneExits.currentPeriod"),
									color: RANG.ruxsatli,
									values: soatlik.map((h) => h.count),
									area: true,
								},
								{
									key: "previous",
									label: t("analytics.zoneExits.previousPeriod"),
									color: RANG.ortacha,
									values: soatlik.map((h) => h.previous),
									dashed: true,
								},
							]}
							yMin={0}
							yMax={soatMax}
							yTicks={[0, soatMax / 2, soatMax]}
							formatY={sonMatni}
							height={160}
							empty={bosh}
						/>
						{/* ⚠️ Seriyalar legendasini `LineChart` o'zi chizadi — bu yerda faqat cho'qqi. */}
						<div className="mt-1">
							{soatChoqqi ? (
								<span className="text-xs" style={{ color: "var(--chart-axis)" }}>
									{t("analytics.zoneExits.hourlyPeak", {
										hour: ikkiXona(soatChoqqi.hour),
										count: sonMatni(soatChoqqi.count),
									})}
								</span>
							) : null}
						</div>
					</ChartCard>
				</div>

				<div data-widget="zones">
					<ChartCard
						title={t("analytics.zoneExits.zonesTitle")}
						subtitle={t("analytics.zoneExits.zonesSubtitle")}
					>
						<HBarList
							empty={bosh}
							rows={data.byZone.map((zona) => ({
								key: zona.zoneId,
								label: zona.zoneName,
								value: zona.total,
								valueText: t("analytics.zoneExits.zoneValue", {
									total: sonMatni(zona.total),
									flagged: sonMatni(zona.flagged),
								}),
								segments: [
									{
										value: Math.max(0, zona.total - zona.flagged),
										color: RANG.ruxsatli,
										label: ruxsatliNom,
									},
									{ value: zona.flagged, color: RANG.ruxsatsiz, label: ruxsatsizNom },
								],
							}))}
						/>
					</ChartCard>
				</div>

				<div data-widget="departments">
					<ChartCard
						title={t("analytics.zoneExits.departmentsTitle")}
						subtitle={t("analytics.zoneExits.departmentsSubtitle")}
						extra={
							<Legend
								items={[
									{ label: ruxsatliNom, color: RANG.ruxsatli },
									{ label: ruxsatsizNom, color: RANG.ruxsatsiz },
								]}
							/>
						}
					>
						{data.byDepartment.length === 0 ? (
							<div className="py-4 text-center text-xs" style={{ color: "var(--chart-axis)" }}>
								{bosh}
							</div>
						) : (
							<div className="flex flex-col gap-2">
								{data.byDepartment.slice(0, TOP_DEPARTMENTS).map((bolim) => {
									const nom = bolim.departmentName ?? t("analytics.generic.noDepartment");
									return (
										<div
											key={bolim.departmentId ?? "__none"}
											data-department-row
											className="grid grid-cols-[minmax(0,7rem)_1fr_auto] items-center gap-2 text-xs"
										>
											<span className="truncate" title={nom}>
												{nom}
											</span>
											<SegmentBar
												showLegend={false}
												segments={[
													{
														key: "permitted",
														value: Math.max(0, bolim.total - bolim.flagged),
														color: RANG.ruxsatli,
														label: ruxsatliNom,
													},
													{
														key: "unpermitted",
														value: bolim.flagged,
														color: RANG.ruxsatsiz,
														label: ruxsatsizNom,
													},
												]}
											/>
											<span className="tabular-nums">
												{foiz(
													bolim.total > 0
														? Math.round((bolim.flagged / bolim.total) * 1000) / 10
														: null
												)}
											</span>
										</div>
									);
								})}
								<div className="mt-1 text-xs" style={{ color: "var(--chart-axis)" }}>
									{t("analytics.zoneExits.scopeShare", {
										share: foiz(
											jamiChiqish > 0
												? Math.round((totalQiymat("unpermitted") / jamiChiqish) * 1000) / 10
												: null
										),
									})}
								</div>
							</div>
						)}
					</ChartCard>
				</div>
			</div>

			<div className="grid gap-3.5 lg:grid-cols-[1.2fr_1fr]">
				<div data-widget="scatter">
					<ChartCard
						title={t("analytics.zoneExits.scatterTitle")}
						subtitle={t("analytics.zoneExits.scatterSubtitle")}
						extra={
							<Legend
								items={[
									{
										label: t("analytics.zoneExits.scatterMostlyPermitted"),
										color: RANG.ruxsatli,
										shape: "dot",
									},
									{
										label: t("analytics.zoneExits.scatterFlagged"),
										color: RANG.ruxsatsiz,
										shape: "dot",
									},
								]}
							/>
						}
					>
						<ScatterPlot
							points={nomlanganNuqtalar}
							xMin={0}
							xMax={xMax}
							yMin={0}
							yMax={yMax}
							xLabel={`0 – ${sonMatni(xMax)} ${t("analytics.zoneExits.scatterX")}`}
							yTicks={[0, yMax / 2, yMax].map((value) => ({ value, label: sonMatni(value) }))}
							quadrant={{
								x: xMax / 2,
								y: yMax / 2,
								highlight: "top-right",
								label: t("analytics.zoneExits.scatterQuadrant"),
							}}
							empty={bosh}
						/>
					</ChartCard>
				</div>

				<div data-widget="top-employees">
					<ChartCard
						title={t("analytics.zoneExits.topTitle")}
						subtitle={t("analytics.zoneExits.topSubtitle")}
						extra={
							<Legend
								items={[
									{ label: t("analytics.zoneExits.total"), color: RANG.ruxsatli, shape: "dot" },
									{ label: ruxsatsizNom, color: RANG.ruxsatsiz, shape: "dot" },
								]}
							/>
						}
					>
						<Lollipop
							rows={data.topEmployees.slice(0, TOP_EMPLOYEES).map((x) => ({
								key: x.employeeId,
								label: x.employeeName,
								total: x.total,
								flagged: x.flagged,
							}))}
							totalColor={RANG.ruxsatli}
							flaggedColor={RANG.ruxsatsiz}
							totalLabel={t("analytics.zoneExits.total")}
							flaggedLabel={ruxsatsizNom}
							empty={bosh}
						/>
					</ChartCard>
				</div>
			</div>
		</div>
	);
}
